import { Chip, Stack, Typography } from '@mui/material';

import ToolTip from '../../../ToolTip/ToolTip';

/**
 * Renders a list of subject/course names as chips.
 * The API sends arrays, but a comma separated string is accepted too so the
 * columns keep working if the payload shape is ever flattened.
 */
const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const Dash = () => <Typography sx={{ color: '#9ca3af' }}>—</Typography>;

const ChipList = ({ value, color = 'default' }) => {
  const items = toList(value);
  if (!items.length) return <Dash />;

  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      {items.map((item, index) => (
        <Chip key={`${item}-${index}`} label={item} size="small" color={color} variant="outlined" />
      ))}
    </Stack>
  );
};

export const CRO_ASSESSMENT_ANALYTICS_TABLE_HEAD = [
  { field: 'sl_no', headerName: 'Sl No', width: 100 },
  { field: 'admn_no', headerName: 'Admn no', width: 130 },
  { field: 'name', headerName: 'Student Name', width: 230 },
  {
    field: 'oga_names',
    headerName: 'OGA Name',
    width: 200,
    /**
     * A student who came through more than one enquiry has more than one OGA.
     * They are joined with commas rather than chipped, because the cell is a
     * single nowrap line -- chips would push the extra names out of sight with
     * nothing to say they exist. The tooltip carries the full list instead.
     */
    renderCell: ({ value }) => {
      const names = toList(value).join(', ');
      if (!names) return <Dash />;
      return <ToolTip content={names} expand={names} />;
    },
  },
  {
    field: 'assessments_held',
    headerName: 'Assessments Held',
    width: 170,
    renderCell: ({ value }) => <Typography sx={{ fontWeight: 600 }}>{value ?? 0}</Typography>,
  },
  {
    field: 'assessed_subjects',
    headerName: 'Assessed Subjects',
    width: 260,
    renderCell: ({ value }) => <ChipList value={value} color="info" />,
  },
  {
    field: 'enrolled_courses',
    headerName: 'Courses Enrolled',
    width: 260,
    renderCell: ({ value }) => <ChipList value={value} color="success" />,
  },
  {
    field: 'enrolled_subjects',
    headerName: 'Subjects Enrolled',
    width: 240,
    renderCell: ({ value }) => <ChipList value={value} color="success" />,
  },
  {
    field: 'not_enrolled_subjects',
    headerName: 'Assessed But Not Enrolled',
    width: 260,
    /**
     * Batch and group courses never record subject enrollments, so an empty gap
     * there means "never recorded", not "took everything". Those rows stay blank
     * rather than flagging every assessed subject as missed.
     */
    renderCell: ({ row, value }) => {
      if (!row?.subjects_tracked) return <Dash />;
      return <ChipList value={value} color="error" />;
    },
  },
  {
    field: 'assessment_report',
    headerName: 'Assessment Report',
    width: 180,
    type: 'action',
    actionType: 'view',
    actionKey: 'Assessment Report',
    buttonName: 'View',
  },
];
